/**
 * View Toggle Component
 * Phase 4: Toggle between Table and Card views
 * 
 * Features:
 * - Persistent view preference via localStorage
 * - Smooth visual transition
 * - Accessible toggle buttons
 * 
 * @version 1.0.0
 * @date January 2026
 */
import React from 'react';
import { LayoutGrid, TableProperties } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';

// LocalStorage key for view preference
const VIEW_PREFERENCE_KEY = 'grn-list-view-mode';

/**
 * Get saved view preference
 * @returns {'card' | 'table'}
 */
export const getSavedViewMode = () => {
  if (typeof window === 'undefined') return 'card';
  return localStorage.getItem(VIEW_PREFERENCE_KEY) || 'card';
};

/**
 * Save view preference
 * @param {'card' | 'table'} mode 
 */
export const saveViewMode = (mode) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(VIEW_PREFERENCE_KEY, mode);
};

/**
 * ViewToggle Component
 * 
 * @param {Object} props
 * @param {'card' | 'table'} props.viewMode - Current view mode
 * @param {Function} props.onViewModeChange - Callback when view mode changes
 * @param {string} props.className - Additional CSS classes
 */
const ViewToggle = ({ viewMode, onViewModeChange, className }) => {
  const handleModeChange = (mode) => {
    saveViewMode(mode);
    onViewModeChange(mode);
  };

  return (
    <div 
      className={cn(
        "inline-flex items-center rounded-lg border bg-gray-100 p-0.5",
        className
      )}
      role="group"
      aria-label="View mode toggle"
      data-testid="view-toggle"
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "h-8 px-3 rounded-md transition-all",
                viewMode === 'card' 
                  ? "bg-white shadow-sm text-blue-600" 
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-200/50"
              )}
              onClick={() => handleModeChange('card')}
              aria-pressed={viewMode === 'card'}
              data-testid="view-toggle-card"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>Card View</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "h-8 px-3 rounded-md transition-all",
                viewMode === 'table' 
                  ? "bg-white shadow-sm text-blue-600" 
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-200/50"
              )}
              onClick={() => handleModeChange('table')}
              aria-pressed={viewMode === 'table'}
              data-testid="view-toggle-table"
            >
              <TableProperties className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>Table View</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

export default ViewToggle;
